import { Key, useMemo, useState } from 'react';
import { Stack, Text, SIZE } from '@quinyx/ui';
import { EditTwoTone } from '@ant-design/icons';
import { FilterValue, SorterResult, SortOrder } from 'antd/lib/table/interface';
import { TableColumnsType, TablePaginationConfig, Tooltip } from 'antd';
import { useNavigate, useLocation, Link } from 'react-router-dom';

import Table from '../../components/Table';
import { getFilterProps } from '../../components/Table/utils';
import { withCustomerLayout } from '../../layouts/customer';
import { ICustomerPaginationContent } from '../../api/interfaces/customer';
import { usePaginatedCustomers } from '../../hooks/customer';
import { IPaginationParams, PaginationOrder } from '../../api/interfaces';
import { createPaginationQueryString } from '../../utils';
import { useTranslate } from '../../hooks/translate';

const SORT_ORDER_MAP: Record<Exclude<SortOrder, null>, PaginationOrder> = {
  ascend: 'asc',
  descend: 'desc',
} as const;

const Customers = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [searchText, setSearchText] = useState<Key>(() => {
    const name = searchParams.get('name');
    return name || '';
  });
  const [searchedColumn, setSearchedColumn] = useState<Key>(() => {
    const name = searchParams.get('name');
    return name ? 'name' : '';
  });
  const { getPaginatedCustomers, paginatedCustomers, isLoading } =
    usePaginatedCustomers();

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<keyof ICustomerPaginationContent, FilterValue | null>,
    sorter:
      | SorterResult<ICustomerPaginationContent>
      | SorterResult<ICustomerPaginationContent>[],
  ) => {
    const nameSearchString = filters.name ? String(filters.name[0]) : undefined;
    const paginationParams: IPaginationParams = {
      name: nameSearchString,
      page: pagination.current && pagination.current,
      size: pagination.pageSize,
    };

    if (!Array.isArray(sorter) && sorter.order != null) {
      paginationParams.sort = {
        name: String(sorter.field),
        order: SORT_ORDER_MAP[sorter.order],
      };
    }

    const newQueryString = createPaginationQueryString(paginationParams);
    navigate(`?${newQueryString}`);
    getPaginatedCustomers(paginationParams);
  };

  const columns: TableColumnsType<ICustomerPaginationContent> = useMemo(
    () => [
      {
        width: 1,
        title: t('common.edit'),
        key: 'action',
        render: ({ domainId }) => (
          <Link
            data-test-id="edit-customer-link"
            to={`/customers/${encodeURIComponent(domainId)}?${searchParams.toString()}`}
            style={{ display: 'flex', justifyContent: 'center' }}>
            <EditTwoTone
              style={{ display: 'flex', justifyContent: 'center' }}
            />
          </Link>
        ),
      },
      {
        title: t('customer.customers'),
        dataIndex: 'name',
        key: 'name',
        ...getFilterProps(
          'name',
          setSearchText,
          setSearchedColumn,
          searchedColumn,
          searchText,
        ),
        sorter: true,
      },
      {
        title: t('customer.active'),
        dataIndex: 'active',
        key: 'active',
        sorter: true,
        render: (active) => (active ? t('common.yes') : t('common.no')),
      },

      {
        title: t('customer.createdDate'),
        dataIndex: 'dateCreated',
        key: 'dateCreated',
        sorter: true,
      },
      {
        title: (
          <Tooltip title={t('customer.numberOfLicencesTooltip')}>
            {t('customer.numberOfLicences')}
          </Tooltip>
        ),
        showSorterTooltip: false,
        dataIndex: 'numberOfLicenses',
        key: 'numberOfLicenses',
        sorter: true,
      },
      {
        title: (
          <Tooltip title={t('customer.numberOfLicencesInUseTooltip')}>
            {t('customer.numberOfLicencesInUse')}
          </Tooltip>
        ),
        showSorterTooltip: false,
        dataIndex: 'numberOfLicensesInUse',
        key: 'numberOfLicensesInUse',
        sorter: true,
      },
      {
        title: (
          <Tooltip title={t('customer.numberOfNonBillableLicencesTooltip')}>
            {t('customer.numberOfNonBillableLicences')}
          </Tooltip>
        ),
        showSorterTooltip: false,
        dataIndex: 'numberOfNonBillableLicenses',
        key: 'numberOfNonBillableLicenses',
        sorter: true,
      },
    ],
    [t, location.search, searchText, searchedColumn],
  );

  return (
    <Stack spacing={SIZE.M}>
      <Text variant="titleStandard" color="PrimaryMain">
        {t('customer.customerList')}
      </Text>
      <Table
        data-test-id="customers-table"
        getRowKey={(customer) => customer.domainId}
        columns={columns}
        isLoading={isLoading}
        data={paginatedCustomers?.content}
        onChange={handleTableChange}
        pagination={{
          total: paginatedCustomers?.totalElements,
          disabled: false,
          pageSize: searchParams.get('size')
            ? Number(searchParams.get('size'))
            : undefined,
          current: searchParams.get('page')
            ? Number(searchParams.get('page'))
            : undefined,
        }}
      />
    </Stack>
  );
};

export default withCustomerLayout(Customers);
